<template>
  <section
    class="relative flex grow flex-col-reverse overflow-hidden rfs:py-48"
  >
    <header class="relative mt-auto rfs:pb-34 rfs:pt-74">
      <div
        class="pointer-events-none absolute left-full top-[35%] w-[30.3%] max-w-344 translate-x-[-53.8%]"
      >
        <MediaPicture :src="LogoGradient" :width="897" :height="1008" />
      </div>
      <h2
        class="relative translate-x-[-2%] font-bold text-primary rfs:text-138"
      >
        Explorer
      </h2>
    </header>
    <ul
      class="wrapper grid grid-cols-1 rfs:gap-24 sm:grid-cols-2 lg:grid-cols-3"
    >
      <li
        v-for="node in processNodes"
        :key="node.id"
        :style="`--node-bg:${node.bg}`"
      >
        <article
          class="relative flex flex-col overflow-hidden bg-[--node-bg] text-white"
        >
          <LogoIcon
            class="pointer-events-none absolute bottom-0 left-full w-[56%] translate-x-[-54%] translate-y-[32%] fill-white opacity-10"
          />
          <NuxtLink
            class="relative rfs:px-20 rfs:pb-38 rfs:pt-24"
            :to="
              localePath({
                name: 'nodes-nodeId',
                params: { nodeId: node.id },
                query: { address: addressToUse },
              })
            "
          >
            <div class="text-14 font-bold">GIO #{{ node.id }}</div>
            <div class="flex justify-center">
              <div>
                <div
                  v-if="node.specialColor"
                  class="pt-6 text-12 font-bold"
                  :style="`color:${node.specialColor}`"
                >
                  SPECIAL EDITION
                </div>
                <h2
                  :class="[
                    'relative font-extrabold rfs:text-50',
                    !node.specialColor && 'pt-18',
                  ]"
                >
                  {{ node.name }}
                  <span
                    v-if="node.specialColor"
                    class="absolute bottom-full left-full translate-x-6 translate-y-1/2"
                    :style="`color:${node.specialColor}`"
                  >
                    +
                  </span>
                </h2>
                <p class="mt-7 text-center text-12">GIO NODE</p>
              </div>
            </div>
          </NuxtLink>
          <div
            class="relative grid grid-cols-6 gap-4 rfs:p-20"
            :style="`background-color:${node.bg2}`"
          >
            <div v-for="i in 6" :key="i" class="relative pb-[100%]">
              <button
                v-if="i > node.itemsCount"
                class="btn btn-white absolute left-0 top-0 flex size-full items-center justify-center bg-white/30 p-0 font-bold text-white hover:bg-white hover:text-primary focus-visible:ring-white/70 active:hover:bg-white rfs:text-18"
                type="button"
              >
                +
              </button>
              <NuxtLink
                v-else
                class="absolute left-0 top-0 flex size-full items-center justify-center bg-white/30 font-bold text-white rfs:text-18"
                :href="
                  localePath({
                    name: 'nodes-nodeId',
                    params: { nodeId: node.id },
                  })
                "
              >
                <template v-if="node.itemsCount > 6 && i === 6">
                  +{{ node.itemsCount - i }}
                </template>
                <NuxtImg
                  v-else
                  :src="`${s3Base}/nft/${node.id}/${holdings[Number(node.id)][i - 1]}.png`"
                  :width="300"
                  :height="300"
                  :placeholder="imgMappings[node.id]"
                  :alt="holdings[Number(node.id)][i - 1]?.toString()"
                />
              </NuxtLink>
            </div>
          </div>
        </article>
      </li>
    </ul>
    <div
      v-if="addressToUse !== address"
      class="mb-40 flex items-center justify-center"
    >
      <TextField
        v-model="walletAddress"
        name="walletAddress"
        placeholder="Enter other wallet adress:"
        label-short="wallet address"
        :class-input="'grow rfs:py-17 text-black'"
        :container-class="'w-[36%]'"
        :rules="{ required: true, eth: true }"
      />
    </div>
  </section>
</template>

<script lang="ts" setup>
import { getAccount, type Config } from '@wagmi/core';
import { useForm } from 'vee-validate';
import { useTypedRoute } from '~/composables/useTypedRoute';
import LogoGradient from 'assets/images/LogoGradient.svg';
import MediaPicture from '~/components/Media/MediaPicture.vue';
import { nodes } from '~/constants/nodes';
import { getAccountHoldings as getAccountHoldingsV2 } from '~/contractInterfaces/gio-node-sub-operator';
import TextField from '~/components/Form/TextField.vue';

import LogoIcon from '~/icons/LogoIcon.vue';
import type { GioNode } from '~/types/GioNode';

import nft2Img from '~/assets/images/GioBasic.png';
import nft5Img from '~/assets/images/GioBasic+.png';
import nft1Img from '~/assets/images/GioPremium.png';
import nft6Img from '~/assets/images/GioPremium+.png';
import nft3Img from '~/assets/images/GioMaster.png';
import nft4Img from '~/assets/images/GioMaster+.png';
import type { ToNode } from '~/constants/routes';

const wagmiConfig = inject('wagmiConfig');
const { defineField } = useForm();
const localePath = useLocalePath();
// const localeLocation = useLocale;
// // localeLocation()
// localeLocation
const route = useTypedRoute<ToNode>();
const router = useRouter();

const { address } = getAccount(wagmiConfig as Config);
const holdings = ref<Record<number, number[]>>({});
const processNodes = ref<GioNode[]>([]);

const addressToUse = computed(() =>
  route?.query?.address ? route?.query?.address : address,
);

const [walletAddress] = defineField('walletAddress');

const {
  public: { S3_BUCKET_URL },
} = useRuntimeConfig();

const s3Base = ref(S3_BUCKET_URL);

const imgMappings = {
  1: nft1Img,
  2: nft2Img,
  3: nft3Img,
  4: nft4Img,
  5: nft5Img,
  6: nft6Img,
  10: `${s3Base.value}/nft/10/1.png`,
  11: `${s3Base.value}/nft/11/1.png`,
} as Record<number | string, string>;

const getHoldingsAndAssign = async () => {
  holdings.value = (await getAccountHoldingsV2(
    addressToUse.value as `0x${string}`,
  )) as Record<number, number[]>;
  nodes?.forEach((node) => {
    node.itemsCount = holdings.value[node?.id as number]?.length || 0;
  });
  processNodes.value = nodes;
};

onMounted(() => {
  getHoldingsAndAssign();

  if (route?.query?.address) {
    walletAddress.value = route?.query?.address;
  }
  if (!route?.query?.address && !address) router.push('/');
});
</script>
